/** 展示格式化：时间、金额、编号、SLA 计时（前端展示，SLA 以后端为准） */

function toDate(value?: string | number | Date | null): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatDateTime(value?: string | number | Date | null, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
}

/** 宽表用短格式：09-07 14:00 */
export function formatShortDateTime(value?: string | number | Date | null, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDate(value?: string | number | Date | null, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatTime(value?: string | number | Date | null, fallback = '—'): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatMoney(value?: number | string | null, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

export function formatNumber(value?: number | string | null, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n.toLocaleString('zh-CN');
}

export function formatPercent(value?: number | string | null, digits = 1, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : `${n.toFixed(digits)}%`;
}

export interface SlaState {
  /** 剩余毫秒；已超时为负 */
  remainingMs: number;
  text: string;
  /** danger: 已超时或 <1h（原型：SLA<1h 脉冲） */
  level: 'safe' | 'soon' | 'over';
}

/** SLA 倒计时：录入→分配 2h、分配→接单 60min、公区领取 2h */
export function slaCountdown(deadline?: string | number | Date | null, now = Date.now()): SlaState | null {
  const d = toDate(deadline);
  if (!d) return null;
  const remainingMs = d.getTime() - now;
  const abs = Math.abs(remainingMs);
  const hours = Math.floor(abs / 3600000);
  const minutes = Math.floor((abs % 3600000) / 60000);
  const body = hours > 0 ? `${hours}h${pad(minutes)}m` : `${minutes}m`;
  if (remainingMs < 0) {
    return { remainingMs, text: `超时 ${body}`, level: 'over' };
  }
  if (remainingMs < 3600000) {
    return { remainingMs, text: `剩 ${body}`, level: 'soon' };
  }
  return { remainingMs, text: `剩 ${body}`, level: 'safe' };
}

/** 入池等待时长（公区 FIFO / >4h 红警） */
export function elapsedText(from?: string | number | Date | null, now = Date.now()): string {
  const d = toDate(from);
  if (!d) return '—';
  const ms = Math.max(0, now - d.getTime());
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h${pad(minutes)}m` : `${minutes}m`;
}

/**
 * 编号规范校验（服务端统一生成，前端仅校验/展示）。订单号 20 位：
 * 运营商段 D 电信 / Y 移动 / L 联通 / G 广电，运营商未知或为空时后端落兜底段 X；
 * 随后 15 位北京时间毫秒时戳 yyMMddHHmmssfff，末尾 4 位随机段。号形由服务端唯一拼装，
 * 前端不自行拼号、也不拿旧号形做校验。
 */
export const ORDER_NO_PATTERN = /^[DYLDGX]\d{19}$/;
export const BATCH_NO_PATTERN = /^B-\d{4}$/;
export const BILL_NO_PATTERN = /^SB-\d{4}-\d{3}-\d$/;
export const SETTLE_BATCH_PATTERN = /^ST-\d{4}W\d{2}$/;
export const ALERT_TICKET_PATTERN = /^AL-\d{4}-\d{2}$/;
export const COURIER_NO_PATTERN = /^DL-\d{4}$/;
export const CARRIER_JOB_NO_PATTERN = /^YS-CD-\d{5}$/;
export const CALL_NO_PATTERN = /^CALL-\d{5}$/;

/** 手机号有效性（录单四项校验之一，前端预校验，后端权威） */
export const MOBILE_PATTERN = /^1[3-9]\d{9}$/;

export function isValidMobile(value: string): boolean {
  return MOBILE_PATTERN.test(String(value).replace(/\s|-/g, ''));
}

/**
 * 收货地读数的唯一出口。排版仍按项负责 seq=1822 那两形；省这一格的裁定变了（群主 seq=1847 改判：订单是全国的，
 * 地址必须带省市区，缺数据由后端接一份全国省市区数据接口查），所以这里 ⛔ 写死「没有省」，只按当下的源渲染：
 * 后端还没产出省 ⇒ 那一截少显示一截，⛔ 拿空串、占位符或「—」顶进串里（项负责 seq=1849）。两形分格符 ⛔ 相同：
 * 列表格「成都/武侯区」用斜杠（⛔ 补空格、⛔ 加省略号，市名去尾「市」而区名原样），
 * 地址行「成都市 · 武侯区 · 人民南路二段88号1204号」用中点。
 * 后端把省补上那天，两枚源键在这里各往前接一截就自然扩成「省/市/区」与「省 · 市 · 区 · 详细地址」，
 * 到那天仍 ⛔ 另开第二枚出口、⛔ 在视图里拼——这正是当初把拼法收进一处的原因。
 */
export interface RegionTextSource {
  /** @deprecated 旧键，后端 seq=320 起改发 receiverProvince/receiverCity/receiverDistrict */
  city?: string | null;
  /** @deprecated 旧键，后端 seq=320 起改发 receiverProvince/receiverCity/receiverDistrict */
  district?: string | null;
  receiverProvince?: string | null;
  receiverCity?: string | null;
  receiverDistrict?: string | null;
}

/** 列表「收货省市区」那一格（群主 seq=320 后端改发三段 receiverProvince/receiverCity/receiverDistrict，旧键 city/district 兼容） */
export function cityDistrictText(source?: RegionTextSource | null, fallback = '—'): string {
  const province = source?.receiverProvince;
  const city = source?.receiverCity || source?.city;
  const district = source?.receiverDistrict || source?.district;
  const parts = [province, shortCityName(city), district].filter((x) => !!x);
  return parts.length ? parts.join('/') : fallback;
}

/** 详情／抽屉那一行「市 · 区 · 详细地址」；street 由调用方决定给明文还是脱敏文本，缺段直接跳过 */
export function regionAddressText(
  source?: RegionTextSource | null,
  street?: string | null,
  fallback = '—',
): string {
  const parts = [source?.city, source?.district, street].filter((x) => !!x);
  return parts.length ? parts.join(' · ') : fallback;
}

function shortCityName(city?: string | null): string {
  const value = String(city ?? '').trim();
  return value.endsWith('市') ? value.slice(0, -1) : value;
}

/**
 * 反馈_06 F3 两枚 additive 新键（receiverStreetName／receiverAddress）的读面形状：行面与详情面同义同值。
 * 上面那两枚出口（cityDistrictText／regionAddressText）的形参与语义一字不动；这里只做「取原文 + 缺键回退」，
 * ⛔ 拼接（拼法归后端：详细地址就是 Address 原文，⛔ 补省市区前缀）、⛔ 掩码（掩码尺在服务端读面一处）、
 * ⛔ 截断（单行省略与看全文归列样式）。
 */
export interface ReceiverDetailSource {
  /** 街道名；未选街道／查无／层级不是 STREET ⇒ 后端给 null */
  receiverStreetName?: string | null;
  /** 详细地址原文（biz_order.Address）；后端 ⛔ 不拼省市区前缀、⛔ 不造占位串 */
  receiverAddress?: string | null;
}

/**
 * 列表「街道」那一格：只认 receiverStreetName 这一枚键。缺键／空串 ⇒ 回 fallback，
 * ⛔ 不拿区县名、street_code 或「收货省市区」那枚出口的结果顶替——那是编读数（F3 冻结件 §1）。
 */
export function receiverStreetText(source?: ReceiverDetailSource | null, fallback = '—'): string {
  return nonEmptyText(source?.receiverStreetName) ?? fallback;
}

/**
 * 列表「详细地址」那一格：receiverAddress 原文照示（后端给什么显示什么）。
 * ⛔ 在串里补省市区前缀、⛔ 按掩码改写（手机号该怎么显示由服务端读面定，前端这里只负责不遮）。
 * 原文为空或缺键 ⇒ 回 fallback。
 */
export function receiverAddressText(source?: ReceiverDetailSource | null, fallback = '—'): string {
  return nonEmptyText(source?.receiverAddress) ?? fallback;
}

/** 取回一枚非空文本键（前后空白不算值）；缺键／空串／非字符串 ⇒ null */
function nonEmptyText(value?: string | null): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}
